import axios from "axios";
import UserService from "../services/user.service";
import authHeader from "./auth-header";
import { config } from "../config";

const ACTOR_TOKEN_BACKUP_KEY = "impersonationActorToken";

class AuthService {
  buildUserSession(tokenData) {
    if (!tokenData || !tokenData.token) {
      return Promise.resolve(null);
    }

    localStorage.setItem("userToken", JSON.stringify(tokenData));

    // getUserAcls() only needs the token, which is already stored above, so it
    // does not have to wait for getUserInfo(). Chaining them made every login
    // pay two sequential round-trips instead of one.
    return Promise.all([
      UserService.getUserInfo(),
      UserService.getUserAcls(),
    ]).then(([userInfo, acls]) => {
      if (!userInfo) {
        return null;
      }

      userInfo.token = tokenData.token;
      userInfo.moreInfo = acls || null;
      localStorage.setItem("user", JSON.stringify(userInfo));
      return userInfo;
    });
  }

  login(user) {
    return axios
      .post(`${config["apiUrl"]}auth/login`, {
        username: user.username,
        password: user.password,
      })
      .then((response) => {
        if (response.status != 200) {
          return null;
        }
        return this.buildUserSession(response.data);
      });
  }

  impersonate(userId) {
    const actorTokenData = JSON.parse(localStorage.getItem("userToken"));
    return axios
      .post(
        `${config["apiUrl"]}auth/impersonate`,
        { user_id: userId },
        { headers: authHeader() }
      )
      .then((response) => {
        if (response.status != 200) {
          return null;
        }

        if (actorTokenData && actorTokenData.token) {
          localStorage.setItem(
            ACTOR_TOKEN_BACKUP_KEY,
            JSON.stringify(actorTokenData)
          );
        }

        return this.buildUserSession(response.data);
      })
      .catch((error) => {
        if (error.response && error.response.status == 401) {
          this.logout();
          window.location = "/login";
        }
        console.log(
          "ERROR:: ",
          error.response ? error.response.data : error.message
        );
        return null;
      });
  }

  stopImpersonation() {
    const actorTokenBackup = JSON.parse(
      localStorage.getItem(ACTOR_TOKEN_BACKUP_KEY)
    );

    return axios
      .post(
        `${config["apiUrl"]}auth/impersonate/stop`,
        {},
        { headers: authHeader() }
      )
      .then((response) => {
        if (response.status != 200) {
          return null;
        }
        localStorage.removeItem(ACTOR_TOKEN_BACKUP_KEY);

        return this.buildUserSession(response.data).then((user) => {
          return user;
        });
      })
      .catch((error) => {
        if (actorTokenBackup && actorTokenBackup.token) {
          localStorage.removeItem(ACTOR_TOKEN_BACKUP_KEY);
          return this.buildUserSession(actorTokenBackup).then((user) => {
            if (user) {
              return user;
            }

            if (error.response && error.response.status == 401) {
              this.logout();
              window.location = "/login";
            }
            console.log(
              "ERROR:: ",
              error.response ? error.response.data : error.message
            );
            return null;
          });
        }

        if (error.response && error.response.status == 401) {
          this.logout();
          window.location = "/login";
        }
        console.log(
          "ERROR:: ",
          error.response ? error.response.data : error.message
        );
        return null;
      });
  }

  updatePhoneAuthorization(authorization) {
    return axios
      .post(`${config["apiUrl"]}auth/phone-authorization`, { authorization }, { headers: authHeader() })
      .then((response) => {
        return response.status == 200 ? response.data : null;
      })
      .catch((error) => {
        console.log("ERROR:: ", error.response ? error.response.data : error.message);
        return null;
      });
  }

  logout() {
    Object.keys(localStorage).forEach((key) => {
      if (key.indexOf("openPagesTabs:") === 0) {
        localStorage.removeItem(key);
      }
    });
    sessionStorage.removeItem("selectedNodes");
    localStorage.removeItem("userToken");
    localStorage.removeItem("user");
    localStorage.removeItem(ACTOR_TOKEN_BACKUP_KEY);
    location.reload(true);
  }
}

export default new AuthService();
